import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  MessageCircle, Search, Plus, Archive, MoreVertical, 
  ArrowLeft, Settings, Users, User, Check, CheckCheck, CheckSquare,
  Image as ImageIcon, Paperclip, Mic, Send, X, Clock, Wifi, WifiOff, Bell, BellOff,
  Newspaper, Phone, Home, UserCircle, UserPlus, Link2, Calendar, FileText, Image,
  ArrowUp, Video, BarChart2, ListTodo, Square, RefreshCw
} from 'lucide-react';
import { conversationsAPI, unreadAPI } from '../../services/messagingAPI';
import ChatWindow from '../../components/messaging/ChatWindow';
import NewConversationModal from '../../components/messaging/NewConversationModal';
import useMessagingWebSocket from '../../hooks/useMessagingWebSocket';
import usePushNotifications from '../../hooks/usePushNotifications';
import { WorkplaceFeed } from '../../components/feed';
import UnifiedPostModal from '../../components/publish/UnifiedPostModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [typingUsers, setTypingUsers] = useState({}); // { conversationId: { userId: userName } }
  
  // Onglet actif pour la navbar style Telegram
  const [activeTab, setActiveTab] = useState('messages'); // 'actu' | 'contacts' | 'messages' | 'groupes'
  // Filtre "Mes pros" dans l'onglet messages
  const [activeFilter, setActiveFilter] = useState('tous'); // 'tous' | 'pros'
  const [searchParams] = useSearchParams();
  
  // Groupes privés (style Workplace)
  const [privateGroups, setPrivateGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showGroupAttachMenu, setShowGroupAttachMenu] = useState(false);
  const [groupInputValue, setGroupInputValue] = useState('');
  const [selectedPostType, setSelectedPostType] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  // ═══ FONCTIONS ENREGISTREMENT VOCAL ═══
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        // Stocker le blob pour l'utiliser dans le modal
        sessionStorage.setItem('voice_note_pending', 'true');
        // Pour l'instant, ouvrir le modal texte avec indication vocale
        setGroupInputValue('🎤 Note vocale enregistrée');
        setSelectedPostType('text');
        setShowPostModal(true);
        toast.success('Note vocale enregistrée !');
        
        // Arrêter les tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Timer pour afficher la durée
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.info('Enregistrement en cours...');
    } catch (error) {
      console.error('Erreur micro:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };
  
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check auth
  const token = localStorage.getItem('ecosystem_token');
  const isAuthenticated = !!token;

  // WebSocket handlers
  const handleNewMessage = useCallback((data) => {
    const { conversation_id, message } = data;
    
    // Mettre à jour la conversation dans la liste
    setConversations(prev => {
      const updated = prev.map(conv => {
        if (conv.id === conversation_id) {
          return {
            ...conv,
            last_message_text: message.content || getMessagePreview(message),
            last_message_type: message.type,
            last_message_at: message.created_at,
            unread_count: selectedConversation?.id === conversation_id 
              ? conv.unread_count 
              : (conv.unread_count || 0) + 1
          };
        }
        return conv;
      });
      
      // Trier par date du dernier message
      return updated.sort((a, b) => 
        new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
      );
    });
    
    // Mettre à jour le compteur total
    if (selectedConversation?.id !== conversation_id) {
      setTotalUnread(prev => prev + 1);
    }
  }, [selectedConversation]);

  const handleTyping = useCallback((data) => {
    const { conversation_id, user_id, user_name, is_typing } = data;
    
    setTypingUsers(prev => {
      const convTyping = { ...prev[conversation_id] } || {};
      if (is_typing) {
        convTyping[user_id] = user_name;
      } else {
        delete convTyping[user_id];
      }
      return { ...prev, [conversation_id]: convTyping };
    });
  }, []);

  const handleReadReceipt = useCallback((data) => {
    // Les accusés de lecture sont gérés dans ChatWindow
  }, []);

  // Initialiser le WebSocket
  const { 
    isConnected, 
    connectionError,
    sendTypingIndicator,
    markMessagesAsRead 
  } = useMessagingWebSocket(handleNewMessage, handleTyping, handleReadReceipt);

  // Push notifications
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    toggleSubscription: togglePush
  } = usePushNotifications();

  const getMessagePreview = (message) => {
    if (message.type === 'voice') return '🎤 Note vocale';
    if (message.type === 'image') return '🖼️ Image';
    if (message.type === 'file') return `📎 ${message.file_name || 'Fichier'}`;
    return message.content?.slice(0, 50) || '';
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const data = await conversationsAPI.getAll(showArchived);
      let convs = data.conversations || [];
      
      // Ajouter des conversations de démo si vide
      if (convs.length === 0) {
        convs = [
          {
            id: 'demo-1',
            type: 'direct',
            group_name: null,
            participants: [{ id: 'user-1', display_name: 'Marie Dupont', avatar: null, user_type: 'particulier' }],
            last_message_text: 'Bonjour ! Je suis intéressée par votre annonce.',
            last_message_type: 'text',
            last_message_at: new Date().toISOString(),
            unread_count: 2,
            is_archived: false
          },
          {
            id: 'demo-2',
            type: 'direct',
            group_name: null,
            participants: [{ id: 'user-2', display_name: 'Pierre Martin', avatar: null, user_type: 'professionnel' }],
            last_message_text: 'Le devis est prêt, je vous l\'envoie.',
            last_message_type: 'text',
            last_message_at: new Date(Date.now() - 3600000).toISOString(),
            unread_count: 0,
            is_archived: false
          },
          {
            id: 'demo-3',
            type: 'group',
            group_name: 'Groupe Famille',
            participants: [
              { id: 'user-3', display_name: 'Sophie', avatar: null },
              { id: 'user-4', display_name: 'Lucas', avatar: null }
            ],
            last_message_text: 'À quelle heure on se retrouve ?',
            last_message_type: 'text',
            last_message_at: new Date(Date.now() - 7200000).toISOString(),
            unread_count: 5,
            is_archived: false
          }
        ];
      }
      
      setConversations(convs);
      setTotalUnread(data.total_unread || convs.reduce((acc, c) => acc + (c.unread_count || 0), 0));
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
      // Fallback avec conversations de démo
      setConversations([
        {
          id: 'demo-1',
          type: 'direct',
          participants: [{ id: 'user-1', display_name: 'Marie Dupont', avatar: null }],
          last_message_text: 'Bonjour ! Comment allez-vous ?',
          last_message_type: 'text',
          last_message_at: new Date().toISOString(),
          unread_count: 1
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, showArchived]);

  useEffect(() => {
    loadConversations();
    // Polling de secours (uniquement si WebSocket déconnecté) - toutes les 30 secondes
    const interval = setInterval(() => {
      if (!isConnected) {
        loadConversations();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [loadConversations, isConnected]);

  // Gérer ?to=userId : créer/ouvrir la conversation directement
  useEffect(() => {
    const toUserId = searchParams.get('to');
    if (!toUserId || !isAuthenticated) return;
    const openConvWithUser = async () => {
      try {
        const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('social_token') || localStorage.getItem('client_token') || '';
        const res = await fetch(`${API_URL}/api/messages/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ type: 'direct', participant_ids: [toUserId], category: 'artisan' })
        });
        if (res.ok) {
          const conv = await res.json();
          await loadConversations();
          navigate(`/messages/${conv.id}`, { replace: true });
        }
      } catch (e) {
        console.error('openConvWithUser error', e);
      }
    };
    openConvWithUser();
  }, [searchParams, isAuthenticated]);

  // Charger les groupes privés (style Workplace)
  const loadPrivateGroups = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingGroups(true);
    try {
      const res = await fetch(`${API_URL}/api/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPrivateGroups(data.groups || []);
      } else {
        // Groupes de démo si l'API échoue
        setPrivateGroups([
          {
            id: 'demo-group-1',
            name: 'Famille Dupont',
            type: 'famille',
            emoji: '👨‍👩‍👧‍👦',
            member_count: 5,
            post_count: 12,
            last_activity: new Date().toISOString()
          },
          {
            id: 'demo-group-2',
            name: 'Les Amis du Sport',
            type: 'sport',
            emoji: '⚽',
            member_count: 8,
            post_count: 24,
            last_activity: new Date(Date.now() - 3600000).toISOString()
          },
          {
            id: 'demo-group-3',
            name: 'Voisins Résidence',
            type: 'voisins',
            emoji: '🏠',
            member_count: 15,
            post_count: 7,
            last_activity: new Date(Date.now() - 86400000).toISOString()
          }
        ]);
      }
    } catch (error) {
      console.error('Erreur chargement groupes:', error);
    } finally {
      setLoadingGroups(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (activeTab === 'groupes') {
      loadPrivateGroups();
    }
  }, [activeTab, loadPrivateGroups]);

  // Sélectionner la conversation depuis l'URL
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) setSelectedConversation(conv);
    }
  }, [conversationId, conversations]);

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const participantMatch = conv.participants?.some(p => 
      p.display_name?.toLowerCase().includes(searchLower)
    );
    const groupMatch = conv.group_name?.toLowerCase().includes(searchLower);
    const messageMatch = conv.last_message_text?.toLowerCase().includes(searchLower);
    return participantMatch || groupMatch || messageMatch;
  });

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    navigate(`/messages/${conv.id}`);
  };

  const handleBack = () => {
    setSelectedConversation(null);
    navigate('/messages');
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Hier';
    } else if (days < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    }
  };

  const getConversationName = (conv) => {
    if (conv.type === 'group' && conv.group_name) {
      return conv.group_name;
    }
    return conv.participants?.map(p => p.display_name).join(', ') || 'Conversation';
  };

  const getConversationAvatar = (conv) => {
    if (conv.type === 'group') {
      return conv.group_avatar || null;
    }
    return conv.participants?.[0]?.avatar || null;
  };

  // Non authentifié
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <MessageCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">AbracadaTchat</h1>
          <p className="text-gray-600 mb-6">
            Connectez-vous pour accéder à vos messages et discuter avec la communauté.
          </p>
          <button
            onClick={() => navigate('/connexion', { state: { from: '/messages' } })}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-xl font-medium hover:from-orange-600 hover:to-orange-700 transition-all"
          >
            Se connecter
          </button>
          <p className="text-sm text-gray-500 mt-4">
            Pas encore de compte ?{' '}
            <button onClick={() => navigate('/inscription')} className="text-orange-600 hover:underline">
              S'inscrire
            </button>
          </p>
        </motion.div>
      </div>
    );
  }

  // Mobile: Afficher soit la liste, soit la conversation - FULLSCREEN avec Portal
  if (isMobile && selectedConversation) {
    return createPortal(
      <div className="fixed inset-0 z-[10002] bg-gray-50">
        <ChatWindow 
          conversation={selectedConversation} 
          onBack={handleBack}
          onConversationUpdate={loadConversations}
          onTyping={sendTypingIndicator}
          onMarkRead={markMessagesAsRead}
          typingUsers={typingUsers[selectedConversation.id]}
          isWebSocketConnected={isConnected}
        />
      </div>,
      document.body
    );
  }

  // Mobile: Afficher le groupe privé sélectionné avec feed Workplace
  if (isMobile && selectedGroup) {
    // Charte graphique : gris clair pour avatars, orange pour accents
    const avatarColor = '#9CA3AF'; // Gris clair premium
    const accentOrange = '#F97316'; // Orange de la charte
    
    return createPortal(
      <div className="fixed inset-0 z-[10002] bg-gray-50 flex flex-col">
        {/* Header du groupe */}
        <header className="bg-white border-b border-gray-200 px-4 py-3" style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSelectedGroup(null)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-lg bg-gray-200">
              {selectedGroup.emoji || '👥'}
            </div>
            <div className="flex-1">
              <h1 className="text-base font-semibold text-gray-900">{selectedGroup.name}</h1>
              <p className="text-xs text-gray-500">{selectedGroup.member_count || 0} membres</p>
            </div>
            {/* Bouton Actualiser */}
            <button
              onClick={() => {
                // Refresh le feed en forçant un re-render
                const currentGroup = selectedGroup;
                setSelectedGroup(null);
                setTimeout(() => setSelectedGroup(currentGroup), 50);
                toast.success('Feed actualisé');
              }}
              className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={18} />
            </button>
          </div>
          
          {/* Onglets du groupe - Style identique aux filtres de l'app */}
          <div className="relative">
            <div className="flex gap-2 mt-3 -mx-4 px-4 pr-12 overflow-x-auto scrollbar-hide">
              <button className="flex-shrink-0 px-4 py-2 bg-orange-500 text-white rounded-xl text-xs font-medium">
                Feed
              </button>
              <button className="flex-shrink-0 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-medium hover:border-gray-300">
                Photos
              </button>
              <button className="flex-shrink-0 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-medium hover:border-gray-300">
                Events
              </button>
              <button className="flex-shrink-0 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-medium hover:border-gray-300">
                Membres
              </button>
              <button className="flex-shrink-0 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-medium hover:border-gray-300 mr-4">
                <Settings className="w-4 h-4" />
              </button>
            </div>
            {/* Flèche orange pour indiquer le scroll */}
            <div className="absolute right-0 top-3 bottom-0 flex items-center bg-gradient-to-l from-white via-white to-transparent pl-8 pr-3">
              <span className="text-orange-500 text-xl font-bold">›</span>
            </div>
          </div>
        </header>

        {/* Feed Workplace avec barre de saisie en bas */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Zone scrollable du feed */}
          <div className="flex-1 overflow-y-auto pb-20">
            <WorkplaceFeed
              contextType="group"
              contextId={selectedGroup.id}
              token={token}
              currentUser={{ id: 'current-user', name: 'Moi' }}
              members={[]}
              accentColor={avatarColor}
              emptyStateMessage="Aucune publication dans ce groupe"
              hideComposer={true}
            />
          </div>
          
          {/* ═══ BARRE DE SAISIE FIXE EN BAS (style WhatsApp/Telegram) ═══ */}
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3">
            <div 
              className="flex items-center rounded-full"
              style={{ 
                height: '52px',
                paddingLeft: '16px',
                paddingRight: '8px',
                backgroundColor: '#ececec',
                border: '2px solid #F97316'
              }}
            >
              {/* Bouton + pour menu attachments */}
              <button
                onClick={() => setShowGroupAttachMenu(!showGroupAttachMenu)}
                className="flex-shrink-0 flex items-center justify-center"
                style={{ color: '#676767' }}
                data-testid="group-attach-btn"
              >
                <Plus size={24} strokeWidth={2} />
              </button>
              
              {/* Input texte */}
              <input
                data-testid="group-post-input"
                type="text"
                value={groupInputValue}
                onChange={(e) => setGroupInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && groupInputValue.trim()) {
                    // Ouvrir le modal avec le texte pré-rempli
                    setSelectedPostType('text');
                    setShowPostModal(true);
                  }
                }}
                placeholder="Quoi de neuf ?"
                className="flex-1 border-none outline-none shadow-none appearance-none focus:outline-none focus:ring-0 focus:border-none"
                style={{
                  marginLeft: '4px',
                  marginRight: '8px',
                  fontSize: '16px',
                  fontWeight: '400',
                  color: '#1a1a1a',
                  caretColor: '#1a1a1a',
                  backgroundColor: 'transparent',
                  WebkitAppearance: 'none',
                  boxShadow: 'none'
                }}
              />
              
              {/* Bouton micro / Stop */}
              {isRecording ? (
                <button
                  data-testid="group-btn-stop"
                  onClick={stopRecording}
                  className="flex-shrink-0 flex items-center gap-2 px-3 py-1 rounded-full"
                  style={{ marginLeft: '8px', backgroundColor: '#EF4444' }}
                >
                  <Square size={16} className="text-white" fill="white" />
                  <span className="text-white text-sm font-medium">{formatRecordingTime(recordingTime)}</span>
                </button>
              ) : (
                <button
                  data-testid="group-btn-voice"
                  onClick={startRecording}
                  className="flex-shrink-0"
                  style={{ marginLeft: '8px', color: '#9a9a9a' }}
                >
                  <Mic size={24} strokeWidth={1.5} />
                </button>
              )}
              
              {/* Bouton envoyer */}
              <button
                data-testid="group-btn-send"
                onClick={() => {
                  if (groupInputValue.trim()) {
                    setSelectedPostType('text');
                    setShowPostModal(true);
                  }
                }}
                className="flex-shrink-0 rounded-full flex items-center justify-center text-white"
                style={{ width: '40px', height: '40px', backgroundColor: '#F97316' }}
              >
                <ArrowUp size={20} strokeWidth={2.5} />
              </button>
            </div>
          </div>
          
          {/* ═══ MENU ATTACHMENTS POPUP ═══ */}
          <AnimatePresence>
            {showGroupAttachMenu && (
              <>
                {/* Overlay */}
                <div 
                  className="absolute inset-0 z-[100]" 
                  style={{ backgroundColor: 'transparent' }}
                  onClick={() => setShowGroupAttachMenu(false)}
                />
                {/* Menu popup */}
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute left-4 right-4 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-[101]"
                  style={{ bottom: '80px' }}
                >
                  <div className="p-3">
                    <div className="flex items-center justify-between px-2 pb-2 border-b border-gray-100 mb-2">
                      <p className="text-sm font-semibold text-gray-700">
                        Créer une publication
                      </p>
                      <button
                        onClick={() => setShowGroupAttachMenu(false)}
                        className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                      >
                        <X size={16} className="text-gray-400" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      {/* Photo */}
                      <button
                        onClick={() => {
                          setShowGroupAttachMenu(false);
                          setSelectedPostType('photo');
                          setShowPostModal(true);
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                          <Image size={22} className="text-green-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Photo</span>
                      </button>
                      
                      {/* Vidéo */}
                      <button
                        onClick={() => {
                          setShowGroupAttachMenu(false);
                          setSelectedPostType('video');
                          setShowPostModal(true);
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Video size={22} className="text-purple-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Vidéo</span>
                      </button>
                      
                      {/* Sondage */}
                      <button
                        onClick={() => {
                          setShowGroupAttachMenu(false);
                          setSelectedPostType('poll');
                          setShowPostModal(true);
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <BarChart2 size={22} className="text-blue-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Sondage</span>
                      </button>
                      
                      {/* Événement */}
                      <button
                        onClick={() => {
                          setShowGroupAttachMenu(false);
                          setSelectedPostType('event');
                          setShowPostModal(true);
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                          <Calendar size={22} className="text-pink-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Événement</span>
                      </button>
                      
                      {/* Tâche */}
                      <button
                        onClick={() => {
                          setShowGroupAttachMenu(false);
                          setSelectedPostType('task');
                          setShowPostModal(true);
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <ListTodo size={22} className="text-orange-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Tâche</span>
                      </button>
                      
                      {/* Document */}
                      <button
                        onClick={() => {
                          setShowGroupAttachMenu(false);
                          setSelectedPostType('document');
                          setShowPostModal(true);
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <FileText size={22} className="text-gray-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Document</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
        
        {/* Modal de publication */}
        <UnifiedPostModal
          isOpen={showPostModal}
          onClose={() => {
            setShowPostModal(false);
            setSelectedPostType(null);
            setGroupInputValue('');
          }}
          context="private"
          contextId={selectedGroup.id}
          contextName={selectedGroup.name}
          token={token}
          currentUser={{ id: 'current-user', name: 'Moi' }}
          initialPostType={selectedPostType}
          initialContent={groupInputValue}
          onPublish={async (postData) => {
            try {
              const endpoint = `${API_URL}/api/groups/${selectedGroup.id}/feed`;
              const headers = { 
                'Authorization': `Bearer ${token}`, 
                'Content-Type': 'application/json' 
              };
              
              const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                  content: postData.content,
                  post_type: postData.type || 'text',
                  poll: postData.poll,
                  event: postData.event,
                  task: postData.task,
                  mentions: []
                })
              });

              if (res.ok) {
                // Forcer le refresh du feed (le composant va se recharger)
                setShowPostModal(false);
                // Petit délai pour laisser le modal se fermer
                setTimeout(() => {
                  // Trigger un re-render du feed en changeant brièvement le groupe
                  const currentGroup = selectedGroup;
                  setSelectedGroup(null);
                  setTimeout(() => setSelectedGroup(currentGroup), 50);
                }, 100);
                return true;
              }
              return false;
            } catch (error) {
              console.error('Erreur publication:', error);
              return false;
            }
          }}
        />
      </div>,
      document.body
    );
  }

  // Filtrer les conversations selon l'onglet actif + filtre pros
  const getFilteredByTab = () => {
    let result = filteredConversations;
    if (activeTab === 'messages') {
      result = result.filter(c => c.type !== 'group');
    } else if (activeTab === 'groupes') {
      result = result.filter(c => c.type === 'group');
    }
    if (activeTab === 'messages' && activeFilter === 'pros') {
      result = result.filter(c => c.category === 'artisan');
    }
    return result;
  };

  const prosUnread = conversations.filter(c => c.category === 'artisan').reduce((acc, c) => acc + (c.unread_count || 0), 0);

  // Compter les messages non lus par type
  const privateUnread = conversations.filter(c => c.type !== 'group').reduce((acc, c) => acc + (c.unread_count || 0), 0);
  const groupUnread = conversations.filter(c => c.type === 'group').reduce((acc, c) => acc + (c.unread_count || 0), 0);

  // Mobile: Interface style Telegram - FULLSCREEN avec Portal
  if (isMobile) {
    const tabConversations = getFilteredByTab();
    
    return createPortal(
      <div className="fixed inset-0 z-[10002] bg-white flex flex-col">
        {/* Header style Telegram */}
        <header className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-100" style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
          <div className="flex items-center gap-3">
            {/* Avatar utilisateur */}
            <div className="w-9 h-9 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">AB</span>
            </div>
            <h1 className="text-lg font-semibold text-gray-900">
              {activeTab === 'actu' && 'Actualités'}
              {activeTab === 'contacts' && 'Contacts'}
              {activeTab === 'messages' && 'Messages'}
              {activeTab === 'groupes' && 'Groupes'}
            </h1>
          </div>
          <div className="flex items-center gap-1">
            {/* Indicateur connexion */}
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-300'}`} />
            {/* Bouton nouveau message */}
            <button 
              onClick={() => setShowNewModal(true)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Plus className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </header>

        {/* Barre de recherche */}
        <div className="px-4 py-2 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Contenu selon l'onglet */}
        <div className="flex-1 overflow-y-auto bg-white">
          {activeTab === 'actu' && (
            /* Onglet Actus - Feeds des groupes privés */
            <div className="p-4">
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Newspaper className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Actualités</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Les publications de vos groupes privés apparaîtront ici
                </p>
                <button className="px-4 py-2 bg-orange-500 text-white rounded-full text-sm font-medium">
                  Rejoindre un groupe
                </button>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            /* Onglet Contacts */
            <div className="divide-y divide-gray-100">
              {/* Contacts de démo */}
              {[
                { id: 1, name: 'Marie Dupont', status: 'En ligne', online: true },
                { id: 2, name: 'Pierre Martin', status: 'Vu récemment', online: false, isPro: true },
                { id: 3, name: 'Sophie Bernard', status: 'En ligne', online: true },
                { id: 4, name: 'Lucas Petit', status: 'Vu il y a 2h', online: false },
              ].map(contact => (
                <button
                  key={contact.id}
                  onClick={() => {
                    // Trouver ou créer une conversation avec ce contact
                    setActiveTab('messages');
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">{contact.name.charAt(0)}</span>
                    </div>
                    {contact.online && (
                      <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" />
                    )}
                    {contact.isPro && (
                      <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-[8px] font-bold px-1 py-0.5 rounded-full">
                        PRO
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{contact.name}</p>
                    <p className="text-xs text-gray-500">{contact.status}</p>
                  </div>
                  <Phone className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          )}

          {(activeTab === 'messages') && (
            /* Onglet Messages */
            <>
              {/* Pills filtre Tous / Mes pros */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
                <button
                  onClick={() => setActiveFilter('tous')}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${activeFilter === 'tous' ? 'bg-[#1E3A5F] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Tous
                </button>
                <button
                  onClick={() => setActiveFilter('pros')}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${activeFilter === 'pros' ? 'bg-[#1E3A5F] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Mes pros
                  {prosUnread > 0 && (
                    <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold ${activeFilter === 'pros' ? 'bg-white text-[#1E3A5F]' : 'bg-orange-500 text-white'}`}>
                      {prosUnread}
                    </span>
                  )}
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : tabConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <MessageCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">Aucun message privé</p>
                  <p className="text-sm text-gray-500 mt-1">Commencez une nouvelle discussion !</p>
                  <button
                    onClick={() => setShowNewModal(true)}
                    className="mt-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-medium transition-colors"
                  >
                    Nouveau message
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {tabConversations.map(conv => (
                    <motion.button
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                      whileTap={{ scale: 0.98 }}
                    >
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        {conv.type === 'group' ? (
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                            <Users className="w-6 h-6 text-white" />
                          </div>
                        ) : getConversationAvatar(conv) ? (
                          <img src={getConversationAvatar(conv)} alt="" className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold">{getConversationName(conv).charAt(0)}</span>
                          </div>
                        )}
                        {conv.participants?.[0]?.user_type === 'professionnel' && (
                          <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-[8px] font-bold px-1 py-0.5 rounded-full">
                            PRO
                          </div>
                        )}
                      </div>

                      {/* Contenu */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`font-semibold truncate ${conv.unread_count > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                            {getConversationName(conv)}
                          </span>
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                            {formatTime(conv.last_message_at)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                          {conv.last_message_text || 'Aucun message'}
                        </p>
                      </div>

                      {/* Badge non lu */}
                      {conv.unread_count > 0 && (
                        <span className="bg-orange-500 text-white text-xs font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5">
                          {conv.unread_count > 99 ? '99+' : conv.unread_count}
                        </span>
                      )}
                    </motion.button>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'groupes' && (
            /* Onglet Groupes Privés - Style Workplace */
            <>
              {loadingGroups ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : privateGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="text-gray-900 font-semibold mb-1">Groupes Privés</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Créez des espaces pour votre famille, vos amis ou vos projets
                  </p>
                  <button
                    onClick={() => setShowCreateGroupModal(true)}
                    className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-medium transition-all shadow-lg shadow-orange-500/25"
                  >
                    <Plus className="w-4 h-4 inline mr-1.5" />
                    Créer un groupe
                  </button>
                </div>
              ) : (
                <div className="p-3 space-y-3">
                  {/* Bouton créer un groupe */}
                  <button
                    onClick={() => setShowCreateGroupModal(true)}
                    className="w-full flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100 hover:border-orange-200 transition-colors"
                  >
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                      <Plus className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Créer un groupe</p>
                      <p className="text-xs text-gray-500">Famille, amis, projet...</p>
                    </div>
                  </button>

                  {/* Liste des groupes */}
                  {privateGroups.map(group => (
                    <motion.button
                      key={group.id}
                      onClick={() => setSelectedGroup(group)}
                      className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-orange-200 hover:shadow-sm transition-all text-left"
                      whileTap={{ scale: 0.98 }}
                    >
                      {/* Avatar groupe */}
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl bg-gray-200">
                        {group.emoji || '👥'}
                      </div>

                      {/* Infos groupe */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{group.name}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {group.member_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {group.post_count || 0} posts
                          </span>
                        </div>
                      </div>

                      {/* Chevron */}
                      <ArrowLeft className="w-5 h-5 text-gray-300 rotate-180" />
                    </motion.button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Bouton flottant recherche style Telegram */}
        <motion.button
          onClick={() => {/* Ouvrir recherche avancée */}}
          className="absolute right-4 bg-white shadow-lg rounded-full p-3 border border-gray-200"
          style={{ bottom: '90px' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Search className="w-5 h-5 text-gray-600" />
        </motion.button>

        {/* Navbar style Telegram en bas */}
        <nav className="bg-white border-t border-gray-200 px-2 py-1 flex items-center justify-around" style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>
          {/* Actu */}
          <button
            onClick={() => setActiveTab('actu')}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              activeTab === 'actu' ? 'text-orange-600' : 'text-gray-500'
            }`}
          >
            <Newspaper className="w-6 h-6" />
            <span className="text-[10px] mt-0.5 font-medium">Actus</span>
          </button>

          {/* Contacts */}
          <button
            onClick={() => setActiveTab('contacts')}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              activeTab === 'contacts' ? 'text-orange-600' : 'text-gray-500'
            }`}
          >
            <UserCircle className="w-6 h-6" />
            <span className="text-[10px] mt-0.5 font-medium">Contacts</span>
          </button>

          {/* Messages */}
          <button
            onClick={() => setActiveTab('messages')}
            className={`relative flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              activeTab === 'messages' ? 'text-orange-600' : 'text-gray-500'
            }`}
          >
            <div className="relative">
              <MessageCircle className="w-6 h-6" />
              {privateUnread > 0 && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">
                  {privateUnread > 99 ? '99+' : privateUnread}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-0.5 font-medium">Messages</span>
          </button>

          {/* Groupes */}
          <button
            onClick={() => setActiveTab('groupes')}
            className={`relative flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              activeTab === 'groupes' ? 'text-orange-600' : 'text-gray-500'
            }`}
          >
            <div className="relative">
              <Users className="w-6 h-6" />
              {groupUnread > 0 && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">
                  {groupUnread > 99 ? '99+' : groupUnread}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-0.5 font-medium">Groupes</span>
          </button>

          {/* Chez moi */}
          <button
            onClick={() => navigate('/cockpit-particulier')}
            className="flex flex-col items-center py-2 px-3 rounded-lg text-gray-500 hover:text-orange-600 transition-colors"
          >
            <Home className="w-6 h-6" />
            <span className="text-[10px] mt-0.5 font-medium">Chez moi</span>
          </button>
        </nav>

        {/* Modal nouvelle conversation */}
        <AnimatePresence>
          {showNewModal && (
            <NewConversationModal
              onClose={() => setShowNewModal(false)}
              onCreated={(conv) => {
                setShowNewModal(false);
                handleSelectConversation(conv);
                loadConversations();
              }}
            />
          )}
        </AnimatePresence>
      </div>,
      document.body
    );
  }

  // Desktop: Layout standard
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Messages</h1>
          {totalUnread > 0 && (
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {totalUnread}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Indicateur de connexion WebSocket */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            isConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {isConnected ? (
              <>
                <Wifi className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">En ligne</span>
              </>
            ) : (
              <>
                <WifiOff className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Hors ligne</span>
              </>
            )}
          </div>
          {/* Bouton notifications push */}
          {pushSupported && (
            <button 
              onClick={togglePush}
              disabled={pushLoading || pushPermission === 'denied'}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                pushSubscribed 
                  ? 'bg-orange-100 text-orange-700' 
                  : pushPermission === 'denied'
                    ? 'bg-red-100 text-red-500 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title={
                pushPermission === 'denied' 
                  ? 'Notifications bloquées dans le navigateur'
                  : pushSubscribed 
                    ? 'Désactiver les notifications'
                    : 'Activer les notifications'
              }
            >
              {pushLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : pushSubscribed ? (
                <Bell className="w-3.5 h-3.5" />
              ) : (
                <BellOff className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">
                {pushSubscribed ? 'Notifs' : 'Notifs off'}
              </span>
            </button>
          )}
          <button 
            onClick={() => setShowNewModal(true)}
            className="p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full transition-colors"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Liste des conversations */}
        <div className={`${isMobile ? 'w-full' : 'w-96'} bg-white border-r border-gray-200 flex flex-col`}>
          {/* Recherche */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une conversation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Filtres */}
          <div className="px-3 py-2 flex gap-2 border-b border-gray-100 flex-wrap">
            <button
              onClick={() => setShowArchived(false)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                !showArchived ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Actives
            </button>
            <button
              onClick={() => setShowArchived(true)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                showArchived ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Archive className="w-3.5 h-3.5" />
              Archives
            </button>
            <div className="h-5 w-px bg-gray-200 self-center" />
            <button
              onClick={() => setActiveFilter(activeFilter === 'pros' ? 'tous' : 'pros')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                activeFilter === 'pros' ? 'bg-[#1E3A5F] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Mes pros
              {prosUnread > 0 && activeFilter !== 'pros' && (
                <span className="w-4 h-4 rounded-full bg-orange-500 text-white text-[9px] flex items-center justify-center font-bold">
                  {prosUnread}
                </span>
              )}
            </button>
          </div>

          {/* Liste */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : getFilteredByTab().length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                <MessageCircle className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm">{activeFilter === 'pros' ? 'Aucun message avec un artisan' : 'Aucune conversation'}</p>
                <button
                  onClick={() => setShowNewModal(true)}
                  className="mt-3 text-orange-600 text-sm font-medium hover:underline"
                >
                  Démarrer une discussion
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {getFilteredByTab().map((conv) => (
                  <motion.button
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`w-full p-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left ${
                      selectedConversation?.id === conv.id ? 'bg-orange-50' : ''
                    }`}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {conv.type === 'group' ? (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                      ) : getConversationAvatar(conv) ? (
                        <img
                          src={getConversationAvatar(conv)}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                      )}
                      {/* Badge Pro */}
                      {conv.participants?.[0]?.user_type === 'professionnel' && (
                        <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                          PRO
                        </div>
                      )}
                    </div>

                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-semibold truncate ${conv.unread_count > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                          {getConversationName(conv)}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {conv.last_message_type === 'voice' && (
                          <Mic className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        {conv.last_message_type === 'image' && (
                          <ImageIcon className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        {conv.last_message_type === 'file' && (
                          <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                          {conv.last_message_text || 'Aucun message'}
                        </p>
                      </div>
                    </div>

                    {/* Badge non lu */}
                    {conv.unread_count > 0 && (
                      <span className="bg-orange-500 text-white text-xs font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5">
                        {conv.unread_count > 99 ? '99+' : conv.unread_count}
                      </span>
                    )}
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Zone de chat (Desktop) */}
        {!isMobile && (
          <div className="flex-1 bg-gray-50">
            {selectedConversation ? (
              <ChatWindow 
                conversation={selectedConversation} 
                onBack={handleBack}
                onConversationUpdate={loadConversations}
                onTyping={sendTypingIndicator}
                onMarkRead={markMessagesAsRead}
                typingUsers={typingUsers[selectedConversation.id]}
                isWebSocketConnected={isConnected}
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MessageCircle className="w-12 h-12 text-gray-300" />
                </div>
                <p className="text-lg font-medium text-gray-700">AbracadaTchat</p>
                <p className="text-sm mt-1">Sélectionnez une conversation pour commencer</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal nouvelle conversation */}
      <AnimatePresence>
        {showNewModal && (
          <NewConversationModal
            onClose={() => setShowNewModal(false)}
            onCreated={(conv) => {
              setShowNewModal(false);
              handleSelectConversation(conv);
              loadConversations();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessagesPage;
